import { getGlobalHitPath, type FacilityHit, type PersonHit } from "@/components/AlgoliaSearch";

export type GlobalSearchEntityHit = PersonHit | FacilityHit;

export type GlobalSearchProximity = {
  sectionId?: string | null;
  sectionName?: string | null;
  state?: string | null;
  recentUrls?: readonly string[];
};

function normalize(value: string | null | undefined): string {
  return value?.trim().toLowerCase() ?? "";
}

function isPersonHit(hit: GlobalSearchEntityHit): hit is PersonHit {
  return hit.object_type === "Person";
}

function hitSectionId(hit: GlobalSearchEntityHit): string {
  if (isPersonHit(hit)) return hit.section_id?.trim() ?? "";
  return hit.section_id?.trim() ?? "";
}

function hitSectionName(hit: GlobalSearchEntityHit): string {
  return normalize(isPersonHit(hit) ? hit.section_name : hit.section_name);
}

function hitState(hit: GlobalSearchEntityHit): string {
  return normalize(hit.state);
}

function recencyIndex(hit: GlobalSearchEntityHit, recentUrls: readonly string[]): number | null {
  const path = getGlobalHitPath(hit);
  if (!path) return null;
  const index = recentUrls.findIndex((url) => url === path || url.startsWith(`${path}/`));
  return index >= 0 ? index : null;
}

function proximityScore(hit: GlobalSearchEntityHit, proximity: GlobalSearchProximity): number {
  const sectionId = proximity.sectionId?.trim() ?? "";
  const sectionName = normalize(proximity.sectionName);
  const state = normalize(proximity.state);
  let score = 0;

  if (sectionId && hitSectionId(hit) === sectionId) score += 4;
  else if (sectionName && hitSectionName(hit) === sectionName) score += 4;
  if (state && hitState(hit) === state) score += 2;

  return score;
}

/** Stable sort: viewed hits by recency, then higher proximity, then original order. */
export function rankGlobalSearchEntityHits<T extends GlobalSearchEntityHit>(
  hits: readonly T[],
  proximity: GlobalSearchProximity
): T[] {
  const recentUrls = proximity.recentUrls ?? [];
  const hasProximity =
    Boolean(proximity.sectionId?.trim()) ||
    Boolean(proximity.sectionName?.trim()) ||
    Boolean(proximity.state?.trim()) ||
    Boolean(recentUrls.length);
  if (!hasProximity || hits.length < 2) return [...hits];

  return hits
    .map((hit, index) => {
      const viewedAt = recencyIndex(hit, recentUrls);
      return {
        hit,
        index,
        recency: viewedAt ?? Number.POSITIVE_INFINITY,
        score: proximityScore(hit, proximity)
      };
    })
    .sort((a, b) => a.recency - b.recency || b.score - a.score || a.index - b.index)
    .map((entry) => entry.hit);
}

function partitionViewedHits<T extends GlobalSearchEntityHit>(
  hits: readonly T[],
  recentUrls: readonly string[]
): { viewed: T[]; rest: T[] } {
  const viewed: T[] = [];
  const rest: T[] = [];
  for (const hit of hits) {
    if (recentUrls.length > 0 && recencyIndex(hit, recentUrls) !== null) viewed.push(hit);
    else rest.push(hit);
  }
  return { viewed, rest };
}

function zipPeopleAndFacilities(
  people: readonly PersonHit[],
  facilities: readonly FacilityHit[]
): GlobalSearchEntityHit[] {
  const mixed: GlobalSearchEntityHit[] = [];
  const length = Math.max(people.length, facilities.length);
  for (let index = 0; index < length; index += 1) {
    const person = people[index];
    const facility = facilities[index];
    if (person) mixed.push(person);
    if (facility) mixed.push(facility);
  }
  return mixed;
}

/**
 * All-tab merge: viewed matches first by recency across people and facilities,
 * then remaining people and facilities interleaved one-for-one.
 */
export function interleaveGlobalSearchEntityHits(
  people: readonly PersonHit[],
  facilities: readonly FacilityHit[],
  proximity: GlobalSearchProximity
): GlobalSearchEntityHit[] {
  const rankedPeople = rankGlobalSearchEntityHits(people, proximity);
  const rankedFacilities = rankGlobalSearchEntityHits(facilities, proximity);
  if (rankedPeople.length === 0) return rankedFacilities;
  if (rankedFacilities.length === 0) return rankedPeople;

  const recentUrls = proximity.recentUrls ?? [];
  const peopleParts = partitionViewedHits(rankedPeople, recentUrls);
  const facilityParts = partitionViewedHits(rankedFacilities, recentUrls);
  const viewed = [...peopleParts.viewed, ...facilityParts.viewed].sort((a, b) => {
    const aRecency = recencyIndex(a, recentUrls) ?? Number.POSITIVE_INFINITY;
    const bRecency = recencyIndex(b, recentUrls) ?? Number.POSITIVE_INFINITY;
    return aRecency - bRecency;
  });

  return [...viewed, ...zipPeopleAndFacilities(peopleParts.rest, facilityParts.rest)];
}
